import { useState, useEffect } from 'react';
import { X, Calendar, Clock, FileText, Building, User, Mail, Phone } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';

interface ConsultantInfo {
  id: string;
  consultant_id?: string;
  first_name: string;
  last_name: string;
  title?: string;
  experience_years: string | number;
  workday_modules: string | string[];
  status?: string;
}

interface InterviewRequestModalProps {
  /** Pass either a job application (from PostJobs) or a consultant profile (from WorkdayTalent) */
  application?: ConsultantInfo;
  /** Legacy prop from WorkdayTalent - same shape as ConsultantInfo but id is the consultant's id */
  consultant?: ConsultantInfo;
  onClose: () => void;
  onSuccess?: () => void;
  theme?: 'light' | 'dark';
}

export default function InterviewRequestModal({ application, consultant, onClose, onSuccess }: InterviewRequestModalProps) {
  const subject = application ?? consultant!;
  const consultantId = subject.consultant_id ?? subject.id;
  const { theme } = useWorkdayClientTheme();
  const isDark = theme === 'dark';
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clientProfile, setClientProfile] = useState<any>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    preferred_interview_date: '',
    preferred_interview_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchClientProfile();
  }, []);

  const fetchClientProfile = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('workday_client_profiles')
        .select(`*, companies ( id, name )`)
        .eq('id', user.id)
        .maybeSingle();

      if (error) console.error('Error fetching client profile:', error);

      setClientProfile(data || {
        id: user.id,
        first_name: 'Pending',
        last_name: 'Setup',
        email: user.email,
        phone: null,
        companies: null
      });
    } catch (error) {
      console.error('Error fetching client profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!formData.notes.trim()) {
      setSubmitError('Please provide notes about your project needs, timeline, and budget.');
      return;
    }

    try {
      setSubmitting(true);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw new Error('Authentication error: ' + authError.message);
      if (!user) throw new Error('Not authenticated. Please log in and try again.');

      const requestedDateTime = formData.preferred_interview_date && formData.preferred_interview_time
        ? `${formData.preferred_interview_date}T${formData.preferred_interview_time}:00`
        : null;

      const modules = typeof subject.workday_modules === 'string'
        ? subject.workday_modules
        : (subject.workday_modules || []).join(', ');

      const requestData = {
        consultant_id: consultantId,
        client_id: user.id,
        consultant_name: `${subject.first_name} ${subject.last_name}`,
        consultant_modules: modules || 'Not specified',
        consultant_experience: String(subject.experience_years) || 'Not specified',
        client_company: clientProfile?.companies?.name || 'Company Pending',
        client_contact_name: `${clientProfile?.first_name || 'Contact'} ${clientProfile?.last_name || 'Pending'}`,
        client_email: clientProfile?.email || user.email || 'noemail@pending.com',
        client_phone: clientProfile?.phone || 'Phone Pending',
        requested_interview_date: requestedDateTime,
        notes: formData.notes.trim(),
        status: 'pending'
      };

      const { error } = await supabase
        .from('interview_requests')
        .insert(requestData)
        .select();

      if (error) throw error;

      if (onSuccess) onSuccess();
      else onClose();
    } catch (error: any) {
      console.error('Interview request submission failed:', error);
      setSubmitError(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = isDark
    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className={`${isDark ? 'bg-gray-900' : 'bg-white'} rounded-lg p-8`}>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0] mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div
        className={`${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Request Interview</h2>
            <p className="text-white/80 text-sm mt-0.5">Schedule with Perplexity Team</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-white/70 transition-colors p-1">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className="font-semibold mb-3">Consultant Details</h3>
            <div className="space-y-1.5">
              {subject.title && (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{subject.title}</p>
              )}
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-[#40E0D0] flex-shrink-0" />
                <span>Modules: {typeof subject.workday_modules === 'string' ? subject.workday_modules : (subject.workday_modules || []).join(', ')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-[#40E0D0] flex-shrink-0" />
                <span>Experience: {subject.experience_years}</span>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className="font-semibold mb-3">Your Company Details</h3>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-[#40E0D0]" />
                <span>{clientProfile?.companies?.name || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-[#40E0D0]" />
                <span>{clientProfile?.first_name} {clientProfile?.last_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-[#40E0D0]" />
                <span>{clientProfile?.email || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-[#40E0D0]" />
                <span>{clientProfile?.phone || 'N/A'}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Preferred Interview Date <span className={`text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>(Optional)</span>
                </label>
                <input
                  type="date"
                  value={formData.preferred_interview_date}
                  onChange={(e) => setFormData({ ...formData, preferred_interview_date: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Preferred Interview Time <span className={`text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>(Optional)</span>
                </label>
                <input
                  type="time"
                  value={formData.preferred_interview_time}
                  onChange={(e) => setFormData({ ...formData, preferred_interview_time: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                <FileText className="w-4 h-4 inline mr-1" />
                Notes <span className="text-red-500">*</span>
              </label>
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Provide details about your project needs, timeline expectations, budget range, and any specific questions for the interview.
              </p>
              <textarea
                required
                rows={6}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Example: We need a Workday HCM consultant for a 6-month implementation project starting in Q3. Budget: $150-200/hr. Key questions: Experience with multi-country implementations?"
                className={`w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`}
              />
            </div>

            {submitError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {submitError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 px-4 py-2.5 rounded-lg border font-medium transition-colors ${isDark ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2.5 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Interview Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
